import { useState } from 'react';
import { SegmentedControl, Stat } from '@salonomy/ui';
import { useTakingsReport, type ReportRange } from '../../data/reports.js';
import { exportCsvFilename } from '../../lib/csv.js';
import { money, moneyWhole } from '../../lib/format.js';
import { ReportCard, type CsvExport } from './shared.js';

/**
 * WS9 — succeeded-payment takings over the range, grouped by day/staff/service. Gross / net / refunded / tips
 * are ALL server figures (ADR-0009: no money math in the browser); we render report.* and the rows verbatim.
 */

type GroupBy = 'day' | 'staff' | 'service';

const GROUP_OPTIONS = [
  { value: 'day' as const, label: 'By day' },
  { value: 'staff' as const, label: 'By staff' },
  { value: 'service' as const, label: 'By service' },
];

export function TakingsCard({ range }: { range: ReportRange }) {
  const [groupBy, setGroupBy] = useState<GroupBy>('day');
  const { data: report, isLoading, isError } = useTakingsReport(range, groupBy);

  const isEmpty = !!report && report.rows.length === 0;

  const csv: CsvExport | null =
    report && report.rows.length > 0
      ? {
          filename: exportCsvFilename(`takings-by-${groupBy}`),
          headers: ['Group', 'Gross', 'Net', 'Refunded', 'Tips', 'Payments'],
          rows: report.rows.map((r) => [
            r.label,
            money(r.grossPence),
            money(r.netPence),
            money(r.refundedPence),
            money(r.tipPence),
            r.paymentCount,
          ]),
        }
      : null;

  return (
    <ReportCard
      title="Takings"
      subtitle="Succeeded-payment revenue over this period. Net is after refunds and fees — every figure is server-computed."
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No succeeded payments in this period."
      csv={csv}
      headerExtra={
        <SegmentedControl options={GROUP_OPTIONS} value={groupBy} onChange={setGroupBy} />
      }
    >
      {report ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Stat label="Gross" value={moneyWhole(report.totalGrossPence)} />
            <Stat label="Net" value={moneyWhole(report.totalNetPence)} />
            <Stat label="Refunded" value={moneyWhole(report.totalRefundedPence)} />
          </div>

          <div className="settings-list">
            {report.rows.map((r) => (
              <div key={r.key} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{r.label}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {r.paymentCount} payment{r.paymentCount === 1 ? '' : 's'} ·{' '}
                    {money(r.refundedPence)} refunded · {money(r.tipPence)} tips
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <Stat label="Gross" value={money(r.grossPence)} />
                  <Stat label="Net" value={money(r.netPence)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </ReportCard>
  );
}
